var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import axios from 'axios';
import { isCalendarDate, sourcePreviewUsable } from './taskMapData';
export default function useFertilizerSourcePreview({ field, season, assessmentDate, plannedApplicationDate, token, enabled, calculationMethod }) {
    const contextKey = JSON.stringify([field, season, assessmentDate, plannedApplicationDate, token, enabled, calculationMethod]);
    const [result, setResult] = React.useState(null);
    const [approval, setApproval] = React.useState(null);
    const [loadingKey, setLoadingKey] = React.useState(null);
    const [failure, setFailure] = React.useState(null);
    const request = React.useRef(null);
    // Rendering also checks the context, so an old result cannot flash before Effect cleanup.
    const preview = (result === null || result === void 0 ? void 0 : result.key) === contextKey ? result.preview : null;
    const allowAging = Boolean(preview && approval === contextKey);
    const loading = loadingKey === contextKey;
    const error = (failure === null || failure === void 0 ? void 0 : failure.key) === contextKey ? failure.message : null;
    const reset = () => {
        var _a;
        (_a = request.current) === null || _a === void 0 ? void 0 : _a.abort();
        request.current = null;
        setResult(null);
        setApproval(null);
        setLoadingKey(null);
        setFailure(null);
    };
    React.useEffect(() => {
        reset();
        return () => { var _a; return (_a = request.current) === null || _a === void 0 ? void 0 : _a.abort(); };
    }, [contextKey]);
    const check = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        reset();
        if (!enabled || !token || !field || !season || !isCalendarDate(assessmentDate) || !isCalendarDate(plannedApplicationDate))
            return;
        const controller = new AbortController();
        request.current = controller;
        setLoadingKey(contextKey);
        try {
            const response = yield axios.post('/fertilizer/source-preview', Object.assign({ field, season, assessment_date: assessmentDate, planned_application_date: plannedApplicationDate }, (calculationMethod ? { calculation_method: calculationMethod } : {})), { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } });
            if (controller.signal.aborted || request.current !== controller)
                return;
            const data = response.data;
            if ((data === null || data === void 0 ? void 0 : data.available) !== true && (data === null || data === void 0 ? void 0 : data.available) !== false)
                throw new Error('Некорректный ответ проверки снимка. Повторите проверку.');
            if (data.available && !sourcePreviewUsable(data, true))
                throw new Error('Не удалось подтвердить источник снимка. Повторите проверку.');
            if (data.available && ((((_a = data.source) === null || _a === void 0 ? void 0 : _a.assessment_date) && data.source.assessment_date !== assessmentDate)
                || (((_b = data.source) === null || _b === void 0 ? void 0 : _b.planned_application_date) && data.source.planned_application_date !== plannedApplicationDate))) {
                throw new Error('Источник относится к другим датам. Повторите проверку снимка.');
            }
            setResult({ key: contextKey, preview: data });
        }
        catch (reason) {
            if (controller.signal.aborted || request.current !== controller)
                return;
            const detail = (_d = (_c = reason.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.detail;
            setFailure({ key: contextKey, message: typeof detail === 'string' ? detail : reason.message || 'Не удалось проверить снимок. Повторите проверку.' });
        }
        finally {
            if (!controller.signal.aborted && request.current === controller)
                setLoadingKey(null);
        }
    });
    return {
        preview, loading, error, allowAging, check, reset,
        setAllowAging: (value) => setApproval(value && preview ? contextKey : null),
        ready: enabled && !loading && sourcePreviewUsable(preview, allowAging),
    };
}
